/* eslint-disable no-use-before-define */
import React, {
  useState, useEffect, useRef
} from 'react';
import { isSafari } from 'react-device-detect';
import { useTranslation, Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { log, LOG_LEVELS } from '@common/config/app_logger';
import { toCamelCase } from '@common/config/utils';
import {
  createPaymentSessionApi,
  processPaymentSessionPaymentApi,
  createCreditCardProcessingPurchaseApi,
  getCreditCardProcessingDataApi
} from '@common/api/api';
import {
  withCaptcha, asyncLoadScript, browserContext, fromMobileApp, isMobile
} from '../../config/utils';
import classes from './classes.module.scss';
import ProcessingStatus from '../../../app/components/purchases/processing_status/processing_status';
import {
  mpTrackPurchaseStartedEvent, mpTrackPurchaseEvent, mpTrackPurchaseFailedEvent, getFollowupParam, renderButtonTitle, isAffiliatePromoPopup, selectCloseButtonActionIfNeed, buildEventData
} from '../../../app/components/purchases/purchases_utils';
import PollUserCreditStatus from '../poll_user_credit_status';
import { setMessageToAppData } from '../../../app/actions/message_to_app';
import { messageToAppKeys } from '../../message_to_app_handler';
import { hidePaymentDetails } from '../../../app/actions/funds';
import { getUser } from '../../../app/actions/user';
import LoadingIndicator from '../../helpers/loading_indicator';

const GooglePay = ({
  currentPaymentOption = null,
  clickSource = '',
  purchaseAmount,
  requiredCredit,
  paymentSourceId,
  children = null,
  disabled = false,
  chatType = '',
  bonus = 0,
  onProcessing = null,
  isContinue = false,
  onPurchaseComplete = null,
  cancelChatOnPP = null,
  type = null,
  hideAffiliatePromoPopupAction = null,
  purchaseFees = null,
  notShowPurchaseSuccessPoppup = false,
  purchaseURL = '',
  advisorId = null
}) => {
  const { t } = useTranslation();
  const [sessionConfig, setSessionConfig] = useState(null);
  const [checkoutJsLoaded, setCheckoutJsLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [userCreditId, setUserCreditId] = useState(null);
  const userCreditIdRef = useRef(null);
  const containerRef = useRef(null);
  const [paymentRes, setPaymentRes] = useState(null);

  const [needPollUserCreditStatus, setNeedPollUserCreditStatus] = useState(false);
  const [gpPayBtnClicked, setGpPayBtnClicked] = useState(false);

  const location = useLocation();

  useEffect(() => {
    // Keep a ref in sync so async callbacks always see the latest value
    userCreditIdRef.current = userCreditId;
  }, [userCreditId]);

  useEffect(() => {
    asyncLoadScript('https://checkout-web-components.checkout.com/index.js', window.CheckoutWebComponents).then(() => {
      setCheckoutJsLoaded(true);
    });
  }, []);

  useEffect(() => {
    const onError = () => {
      log(LOG_LEVELS.INFO, 'Google PAy', 'Global JS Error onError');
    };
    const onUnhandledRejection = () => {
      log(LOG_LEVELS.INFO, 'Google PAy', 'Global JS Error onUnhandledRejection');
    };

    const onOffline = (e) => {
      log(LOG_LEVELS.INFO, 'Google PAy', 'Global JS Error onOffline');
      onGoogleError(e);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const trackPurchaseStarted = (dataResponse) => {
    if (fromMobileApp) {
      const purchaseStartedProps = buildEventData({
        type, purchaseAmount, bonus, currentPaymentOption, chatType, purchaseFees, clickSource
      });
      window.store.dispatch(setMessageToAppData({
        sentToAppMessage:{
          [messageToAppKeys.action]: messageToAppKeys.analytics,
          [messageToAppKeys.params]: {
            analytic_events: {
              'purchase started': {
                ...purchaseStartedProps
              }
            }
          }
        }
      }));
      return;
    }
    mpTrackPurchaseStartedEvent({
      type, purchaseAmount: parseFloat(purchaseAmount), bonus, purchaseFees, dispatch: window.store.dispatch, ...dataResponse, currentPaymentOption, chatType, location, advisorId
    });
  };

  const handlePurchase = (purchaseData) => {
    const {
      conversionEventId, extraEventParams, okMessage, cashbackAmount, pathname
    } = purchaseData;

    if (fromMobileApp) {
      const purchaseProps = buildEventData({
        type, purchaseAmount, bonus, currentPaymentOption, chatType, extraEventParams, clickSource
      });
      window.store.dispatch(setMessageToAppData({
        sentToAppMessage: {
          [messageToAppKeys.action]: messageToAppKeys.onPayCompeted,
          [messageToAppKeys.params]: {
            [messageToAppKeys.okMessage]: okMessage,
            [messageToAppKeys.cashback]: cashbackAmount ? parseFloat(cashbackAmount).toFixed(2) : null,
            analytic_events: {
              Purchase: {
                ...purchaseProps
              }
            }
          }
        }
      }));
      // need for send onSubmit message to app for ADVISOR BONUS
      if (onPurchaseComplete && notShowPurchaseSuccessPoppup) {
        onPurchaseComplete();
      }
    } else {
      mpTrackPurchaseEvent({
        type, purchaseAmount, bonus, purchaseFees, conversionEventId,
        extraEventParams, currentPaymentOption, chatType, pathname, location, advisorId
      });
      window.store.dispatch(getUser());
    }
  };

  const handleError = (response, error = null) => {
    const { errorMessage, MPEventErrorMessage } = response || {};
    const props = {
      ...response,
      errorMessage: MPEventErrorMessage ?? errorMessage ?? error
    };
    if (fromMobileApp) {
      return;
    }
    mpTrackPurchaseFailedEvent({
      ...props, type, purchaseAmount, bonus, purchaseFees, chatType, currentPaymentOption, location, advisorId
    });
  };

  const startPayment = () => {
    setProcessing(true);
    const params = {
      use_reference_transaction: true,
      with3ds: true,
      ...getFollowupParam({ isContinue, type }),
      ...browserContext
    };

    withCaptcha('checkout', (token) => {
      getCreditCardProcessingDataApi({ url: purchaseURL, params }).then((res) => {
        trackPurchaseStarted(res);
        createCreditCardProcessingPurchaseApi({
          creditRequested: requiredCredit,
          usePaymentSourceId: paymentSourceId,
          ...browserContext,
          ...res.requiredParams
        }).then(({ id, error }) => {
        // id is the user credit id
          log(LOG_LEVELS.INFO, 'Google PAy', 'createCreditCardProcessingPurchaseApi', { id, error });
          if (!id) {
            log(LOG_LEVELS.INFO, 'Google PAy', 'error', { error });
            setPaymentRes({ errorMessage: error });
            return;
          }
          setUserCreditId(id);
          userCreditIdRef.current = id;
          // this is the user credit id
          createPaymentSessionApi(paymentSourceId, id, token).then(({ paymentSession, publicKey, environment }) => {
            log(LOG_LEVELS.INFO, 'Google PAy', 'setSessionConfig', { paymentSession, publicKey, environment });
            setSessionConfig({ paymentSession, publicKey, environment });
          }).catch((err) => {
            try {
              err.json().then((data) => {
                log(LOG_LEVELS.INFO, 'Google PAy', 'error', { data });
                onGoogleError(data);
              }).catch((e) => {
                log(LOG_LEVELS.INFO, 'Google PAy', 'error', { e });
                onGoogleError(e);
              });
            } catch (e) {
              log(LOG_LEVELS.INFO, 'Google PAy', 'error', { e });
              onGoogleError(e);
            }
          });
        }).catch((error) => {
        // spending limit errors will be here
          try {
            error.json().then((data) => {
              const responce = toCamelCase(data);
              onGoogleError(responce);
            });
          } catch (e) {
            log(LOG_LEVELS.INFO, 'Google PAy', 'error', { e });
            onGoogleError(e);
          }
        });
      }).catch((e) => {
        log(LOG_LEVELS.INFO, 'Google PAy', 'error', { e });
        onGoogleError(e);
      });
    });
  };

  useEffect(() => {
    if (!sessionConfig || !containerRef.current || !checkoutJsLoaded) { return; }
    async function init() {
      try {
        const { paymentSession, publicKey, environment } = sessionConfig;
        log(LOG_LEVELS.INFO, 'Google PAy', 'loadCheckoutWebComponents', { paymentSession, publicKey, environment });
        const {
          id, _links, paymentSessionToken, paymentSessionSecret
        } = paymentSession;
        const paymentSessionOriginal = {
          id, _links, payment_session_token: paymentSessionToken, payment_session_secret: paymentSessionSecret
        };

        let checkout;

        try {
          checkout = await window.CheckoutWebComponents({
            paymentSession: paymentSessionOriginal,
            publicKey,
            environment,
            locale: 'en',
            componentOptions: {
              googlepay: {
                buttonType: 'pay',
                buttonColor: 'black'
              }
            },
            appearance: {
              colorBorder: '#fff'
            }
          });
        } catch (e) {
          log(LOG_LEVELS.INFO, 'Google PAy', 'error checkout', { e });
          onGoogleError(e);
        }

        if (!checkout) { return; }

        if (isSafari) {
          const testPopupAfterAsyncDelay = async () => new Promise((resolve) => {
            setTimeout(() => {
              const testWin = window.open('about:blank', '_blank', 'width=1,height=1');
              if (!testWin) return resolve(false);
              testWin.close();
              resolve(true);
              return null;
            }, 300);
          });

          const allowed = await testPopupAfterAsyncDelay();
          if (!allowed) {
            const enableGooglePayErrorMessage = (
              <div className={ classes.googlePayToUseErrorMessageContainer }>
                <Trans
                  i18nKey={ isMobile() ? 'google_pay.to_use_google_pay_on_safari_you_need_to_allow_pop_ups_mobile' : 'google_pay.to_use_google_pay_on_safari_you_need_to_allow_pop_ups_desktop' }
                  components={ { googlePayToUseActionList: <div className={ classes.googlePayToUseActionList } /> } }
                />
              </div>
            );
            onGoogleError({
              errorMessage: enableGooglePayErrorMessage,
              errorTitle: t('google_pay.enable_google_pay'),
              MPEventErrorMessage: 'To use Google Pay on Safari, you need to allow pop-ups'
            });
            return;
          }
        }

        let flow;
        try {
          flow = checkout.create('flow', {
            onChange: () => {
              log(LOG_LEVELS.INFO, 'Google PAy', 'flowComponent onChange');
            },
            onReady: () => {
              log(LOG_LEVELS.INFO, 'Google PAy', 'flowComponent onReady');
              setReady(true);
            },
            onAuthorized: () => {
              log(LOG_LEVELS.INFO, 'Google PAy', 'flowComponent onAuthorized');
              setProcessing(true);
            },
            onSubmit: () => {
              log(LOG_LEVELS.INFO, 'Google PAy', 'flowComponent submit');
            },
            onPaymentCompleted: async (_self, paymentResponse) => {
              log(LOG_LEVELS.INFO, 'Google PAy', 'flowComponent payment completed paymentResponse', { paymentResponse });

              const { id: paymentId } = paymentResponse;
              const latestUserCreditId = userCreditIdRef.current;

              log(LOG_LEVELS.INFO, 'Google PAy', 'flowComponent payment completed processPaymentSessionPaymentApi params', { paymentId, latestUserCreditId, paymentSessionOriginalId: paymentSessionOriginal.id });

              processPaymentSessionPaymentApi(paymentSourceId, paymentId, latestUserCreditId, paymentSessionOriginal.id).then(() => {
                setNeedPollUserCreditStatus(true);
              });
            },
            onError: async (_self, error) => {
              log(LOG_LEVELS.INFO, 'Google PAy', 'flowComponent error', { error });
              onGoogleError(error);
            }
          });
        } catch (e) {
          log(LOG_LEVELS.INFO, 'Google PAy', 'error flow', { e });
          onGoogleError(e);
        }

        try {
          flow.mount(containerRef.current);
        } catch (e) {
          log(LOG_LEVELS.INFO, 'Google PAy', 'error flow.mount', { e });
          onGoogleError(e);
        }
      } catch (error) {
        log(LOG_LEVELS.INFO, 'Google PAy', 'error !!!!!!!!!!!!!!!!!!!', { error });
        onGoogleError(error);
      }
    }
    init();
  }, [sessionConfig, containerRef.current, checkoutJsLoaded]);

  useEffect(() => {
    if (containerRef.current && ready && gpPayBtnClicked) {
      setTimeout(() => {
        containerRef.current.querySelector('button[aria-label="Pay with GPay"]').click();
        setTimeout(() => { // need for delay before hide loading indicator
          setReady(false);
          setGpPayBtnClicked(false);
          setProcessing(false); // need for hide processing when google pay popup presented
        }, 1000);
      }, 1000);
    }
  }, [containerRef.current, ready, gpPayBtnClicked]);

  const doClick = () => {
    if (disabled || processing) { return; }
    startPayment();

    setGpPayBtnClicked(true);
  };

  const updateProcesing = (p, processingResponse = null) => {
    setProcessing(p);
    setNeedPollUserCreditStatus(false);
    if (onProcessing) {
      onProcessing(p, processingResponse);
    }
  };

  const onGoogleError = (err) => {
    let errorResponce = {
      ...err,
      errorMessage: err?.details?.requestErrorCodes?.[0] || err?.errorMessage || 'error',
      errorCode: err?.code || null,
      errorName: err?.message || null,
      errorType: err?.errorType,
      errorTitle: err?.errorTitle
    };

    if (err.type === 'offline') {
      errorResponce = { errorMessage: t('chat.network_seems_to_be_offline'), errorCode: 'error', errorName: t('chat.network_seems_to_be_offline') };
    }

    setPaymentRes(errorResponce);
    updateProcesing(false, errorResponce);
    handleError(errorResponce);
  };

  const onUserCreditStatusChange = (isSuccess, response) => {
    if (isSuccess) {
      handlePurchase(response);
    } else {
      handleError(response);
    }
    setPaymentRes(response);
    updateProcesing(false, response);
  };

  const dissmiss = () => {
    window.store.dispatch(hidePaymentDetails());
  };

  const onConfirmButtonClick = () => {
    dissmiss();
    setPaymentRes(null);
    if (onPurchaseComplete) {
      onPurchaseComplete();
    }
  };

  const onCloseButtonClick = () => {
    dissmiss();
    setPaymentRes(null);
    selectCloseButtonActionIfNeed({ type, cancelChatOnPP, hideAffiliatePromoPopup: hideAffiliatePromoPopupAction });
  };

  const onErrorButtonClick = () => {
    dissmiss();
    setPaymentRes(null);
    selectCloseButtonActionIfNeed({ type, cancelChatOnPP, hideAffiliatePromoPopup: hideAffiliatePromoPopupAction });
  };

  const getGooglePayButtonClassName = () => {
    if (processing) {
      if (fromMobileApp) return classnames(classes.googlePayButton, classes.googlePayButtonLoading, classes.googlePayButtonLoadingForMobileApp);
      return classnames(classes.googlePayButton, classes.googlePayButtonLoading);
    }
    return classes.googlePayButton;
  };

  const paymentPopup = () => {
    if (fromMobileApp) return null;
    if (processing || !!paymentRes) {
      return (
        <ProcessingStatus
          purchaseResponce={ paymentRes || {} }
          visible
          purchaseAmount={ purchaseAmount }
          isAffiliatePromoPopup={ isAffiliatePromoPopup({ type }) }
          isCreditCard
          chatType={ chatType }
          t={ t }
          onConfirm={ onConfirmButtonClick }
          onClose={ onCloseButtonClick }
          onRetry={ onErrorButtonClick }
          buttonTitle={ renderButtonTitle({ isContinue, type, t }) }
          bonus={ bonus }
          notShowPurchaseSuccessPoppup={ notShowPurchaseSuccessPoppup }
        />
      );
    }
    return null;
  };

  const processingRender = () => {
    if (processing && needPollUserCreditStatus) {
      return (
        <div>
          {
            userCreditId ? (
              <PollUserCreditStatus id={ userCreditId } onChange={ onUserCreditStatusChange } />
            ) : null
          }
        </div>
      );
    }
    return null;
  };

  return (
    <div className={ classes.flow }>
      <div ref={ containerRef } style={ { display: 'none' } } />
      { paymentPopup() }
      { processingRender() }
      <button type="button" className={ getGooglePayButtonClassName() } onClick={ doClick }>
        { processing ? <LoadingIndicator /> : children }
      </button>
    </div>
  );
};

export default GooglePay;
